import { UserStore } from "./user.store";
import { WorkspaceStore } from "./workspace.store";
import { ProjectStore } from "./project.store";
import { InstanceStore } from "./instance.store";
import { RouterStore } from "./router.store";
import { InvitationStore } from "./invitation.store";
import { IssueStore } from "./issue.store";
import { IssueViewPreferencesStore } from "./issue-view-preferences.store";
import { IssueDetailStore } from "./issue-detail.store";
import { EventDraftStore } from "./event-draft.store";
import { WorkItemViewPreferencesStore } from "./work-item-view-preferences.store";
import { ProjectsListViewPreferencesStore } from "./projects-list-view-preferences.store";
import { ProgramsListViewPreferencesStore } from "./programs-list-view-preferences.store";
import { NotificationStore } from "./notification.store";
import { listenForCacheHints } from "./cache-hints";
import { ViewStore } from "./view.store";
import { CommandPaletteStore } from "./command-palette.store";
import { DependentShiftPromptStore } from "./dependent-shift-prompt.store";
import { TimelineStore } from "./timeline.store";
import { TimelinePreferencesStore } from "./timeline-preferences.store";
import { WorkspaceSettingsStore } from "./workspace-settings.store";
import { ProfileSettingsStore } from "./profile-settings.store";
import { WorkspaceMembersStore } from "./workspace-members.store";
import { WorkspaceRolesStore } from "./workspace-roles.store";
import { WorkspaceCalendarsStore } from "./workspace-calendars.store";
import { WorkspaceSkillsStore } from "./workspace-skills.store";
import { WorkspaceAvailabilityStore } from "./workspace-availability.store";
import { ProjectMembersStore } from "./project-members.store";
import { WorkspaceTeamsStore } from "./workspace-teams.store";
import { WorkHoursStore } from "./work-hours.store";
import { ProjectTemplatesStore } from "./project-templates.store";
import { WorkitemTemplateAttachmentsStore } from "./workitem-template-attachments.store";
import { WorkspaceTemplatesStore } from "./workspace-templates.store";
import { CapacityPlanningStore } from "./capacity-planning.store";
import { IntakeStore } from "./intake.store";
import { DashboardStore } from "./dashboard.store";
import { ReportStore } from "./report.store";
import { FeatureFlagStore } from "./feature-flag.store";
import { RequestWorkflowStore } from "./request-workflow.store";
import { FormWorkflowStore } from "./form-workflow.store";
import { AuthService } from "@/services/auth.service";
import { UserService } from "@/services/user.service";
import { WorkspaceService } from "@/services/workspace.service";
import { ProjectService } from "@/services/project.service";
import { PermissionService } from "@/services/permission.service";
import { ProjectCustomFieldsService } from "@/services/project-custom-fields.service";
import { WorkItemCustomFieldsService } from "@/services/work-item-custom-fields.service";
import { ProjectMemberService } from "@/services/project-member.service";
import { WorkItemStateService } from "@/services/work-item-state.service";
import { ProjectStateService } from "@/services/project-state.service";
import { RequestStatusService } from "@/services/request-status.service";
import { RequestReasonService } from "@/services/request-reason.service";
import { FavoriteService } from "@/services/favorite.service";
import { MyWorkService } from "@/services/my-work.service";
import { InstanceService } from "@/services/instance.service";
import { InvitationService } from "@/services/invitation.service";
import { IssueArchiveService } from "@/services/issue-archive.service";
import { WorkItemService } from "@/services/work-item.service";
import { IssueCommentService } from "@/services/issue-comment.service";
import { MentionableUsersService } from "@/services/mentionable-users.service";
import { LinkPreviewService } from "@/services/link-preview.service";
import { SupportAccessService } from "@/services/support-access.service";
import { IssueAttachmentService } from "@/services/issue-attachment.service";
import { IssueMetadataService } from "@/services/issue-metadata.service";
import { EventService } from "@/services/event.service";
import { NotificationService } from "@/services/notification.service";
import { ViewService } from "@/services/view.service";
import { WorkspaceRoleService } from "@/services/workspace-role.service";
import { WorkspaceJobRoleService } from "@/services/workspace-job-role.service";
import { WorkspaceExportService } from "@/services/workspace-export.service";
import { WorkspaceWebhookService } from "@/services/workspace-webhook.service";
import { WorkspaceAIProviderService } from "@/services/workspace-ai-provider.service";
import { LmsConnectionService } from "@/services/lms-connection.service";
import { IntegrationCapabilityService } from "@/services/integration-capability.service";
import { MsTeamsConnectionService } from "@/services/msteams-connection.service";
import { MsTeamsLinkService } from "@/services/msteams-link.service";
import { LmsLinkService } from "@/services/lms-link.service";
import { LmsRecommendationService } from "@/services/lms-recommendation.service";
import { LmsResultsService } from "@/services/lms-results.service";
import { WorkspaceSsoService } from "@/services/workspace-sso.service";
import { WorkspacePasswordPolicyService } from "@/services/workspace-password-policy.service";
import { WorkspaceSessionPolicyService } from "@/services/workspace-session-policy.service";
import { WorkspaceTeammatesService } from "@/services/workspace-teammates.service";
import { WorkspaceTeamSetupAreasService } from "@/services/workspace-team-setup-areas.service";
import { WorkspaceCalendarService } from "@/services/workspace-calendar.service";
import { AuditService } from "@/services/audit.service";
import { WorkspaceSkillService } from "@/services/workspace-skill.service";
import { WorkspaceAvailabilityService } from "@/services/workspace-availability.service";
import { WorkspaceCapacityService } from "@/services/workspace-capacity.service";
import { TimeOffService } from "@/services/time-off.service";
import { WorkspaceTeamService } from "@/services/workspace-team.service";
import { WorkspaceAssetService } from "@/services/workspace-asset.service";
import { UserAssetService } from "@/services/user-asset.service";
import { TimezoneService } from "@/services/timezone.service";
import { CurrencyService } from "@/services/currency.service";
import { WorklogService } from "@/services/worklog.service";
import { WorkHoursService } from "@/services/work-hours.service";
import { ProjectTemplateService } from "@/services/project-template.service";
import { WorkspaceTemplateService } from "@/services/workspace-template.service";
import { ProjectCapacityService } from "@/services/project-capacity.service";
import {
  IntakeService,
  IntakeSubmissionActivityService,
  IntakeSubmissionCommentService,
} from "@/services/intake.service";
import { SlaService } from "@/services/sla.service";
import { RequestPrioritizationService } from "@/services/request-prioritization.service";
import { AutomationSettingsService } from "@/services/automation-settings.service";
import { DashboardService } from "@/services/dashboard.service";
import { ReportService } from "@/services/report.service";
import { CapacityRebalanceService } from "@/services/capacity-rebalance.service";
import { ProjectWorkflowService } from "@/services/project-workflow.service";
import {
  GoalsService,
  ProcessesService,
  ProgramActivityService,
  ProgramCommentService,
  ProgramsService,
  ProjectActivityService,
  ProjectCommentService,
  ProjectLinkedSubmissionsService,
  ProjectV3Service,
} from "@/services/projects-v3.service";
import { ProcessesStore } from "./projects-v3.store";
import { WorkspaceCustomFieldService } from "@/services/workspace-custom-field.service";
import { WorkspaceCategoriesService } from "@/services/workspace-categories.service";
import { WorkspaceCustomFieldsStore } from "./workspace-custom-fields.store";
import { RequestWorkflowService } from "@/services/request-workflow.service";
import { FormWorkflowService } from "@/services/form-workflow.service";
import { TimelineService } from "@/services/timeline.service";
import {
  ReportingAgentSocket,
  type IReportingAgentSocket,
} from "@/services/reporting-agent.socket";
import { ReportingAgentStore } from "./reporting-agent.store";
import { ReportingAgentService } from "@/services/reporting-agent.service";

export class RootStore {
  authService: AuthService;
  userService: UserService;
  workspaceService: WorkspaceService;
  projectService: ProjectService;
  projectCustomFieldsService: ProjectCustomFieldsService;
  workItemCustomFieldsService: WorkItemCustomFieldsService;
  projectMemberService: ProjectMemberService;
  workItemStateService: WorkItemStateService;
  projectStateService: ProjectStateService;
  requestStatusService: RequestStatusService;
  requestReasonService: RequestReasonService;
  favoriteService: FavoriteService;
  myWorkService: MyWorkService;
  instanceService: InstanceService;
  invitationService: InvitationService;
  workItemService: WorkItemService;
  issueService: WorkItemService;
  issueArchiveService: IssueArchiveService;
  issueCommentService: IssueCommentService;
  mentionableUsersService: MentionableUsersService;
  linkPreviewService: LinkPreviewService;
  supportAccessService: SupportAccessService;
  issueAttachmentService: IssueAttachmentService;
  issueMetadataService: IssueMetadataService;
  eventService: EventService;
  notificationService: NotificationService;
  viewService: ViewService;
  workspaceRoleService: WorkspaceRoleService;
  workspaceJobRoleService: WorkspaceJobRoleService;
  workspaceExportService: WorkspaceExportService;
  workspaceWebhookService: WorkspaceWebhookService;
  workspaceAIProviderService: WorkspaceAIProviderService;
  lmsConnectionService: LmsConnectionService;
  integrationCapabilityService: IntegrationCapabilityService;
  msTeamsConnectionService: MsTeamsConnectionService;
  msTeamsLinkService: MsTeamsLinkService;
  lmsLinkService: LmsLinkService;
  lmsRecommendationService: LmsRecommendationService;
  lmsResultsService: LmsResultsService;
  workspaceSsoService: WorkspaceSsoService;
  workspacePasswordPolicyService: WorkspacePasswordPolicyService;
  workspaceSessionPolicyService: WorkspaceSessionPolicyService;
  workspaceTeammatesService: WorkspaceTeammatesService;
  workspaceTeamSetupAreasService: WorkspaceTeamSetupAreasService;
  workspaceCalendarService: WorkspaceCalendarService;
  auditService: AuditService;
  workspaceSkillService: WorkspaceSkillService;
  workspaceAvailabilityService: WorkspaceAvailabilityService;
  workspaceCapacityService: WorkspaceCapacityService;
  timeOffService: TimeOffService;
  workspaceTeamService: WorkspaceTeamService;
  workspaceAssetService: WorkspaceAssetService;
  userAssetService: UserAssetService;
  timezoneService: TimezoneService;
  currencyService: CurrencyService;
  worklogService: WorklogService;
  workHoursService: WorkHoursService;
  projectTemplateService: ProjectTemplateService;
  workspaceTemplateService: WorkspaceTemplateService;
  projectCapacityService: ProjectCapacityService;
  intakeService: IntakeService;
  requestActivityService: IntakeSubmissionActivityService;
  requestCommentService: IntakeSubmissionCommentService;
  slaService: SlaService;
  requestPrioritizationService: RequestPrioritizationService;
  automationSettingsService: AutomationSettingsService;
  dashboardService: DashboardService;
  reportService: ReportService;
  capacityRebalanceService: CapacityRebalanceService;
  projectWorkflowService: ProjectWorkflowService;
  programsService: ProgramsService;
  goalsService: GoalsService;
  workspaceCustomFieldService: WorkspaceCustomFieldService;
  workspaceCategoriesService: WorkspaceCategoriesService;
  processesService: ProcessesService;
  permissionService: PermissionService;
  programActivityService: ProgramActivityService;
  programCommentService: ProgramCommentService;
  projectActivityService: ProjectActivityService;
  projectCommentService: ProjectCommentService;
  projectLinkedSubmissionsService: ProjectLinkedSubmissionsService;
  projectV3Service: ProjectV3Service;
  requestWorkflowService: RequestWorkflowService;
  formWorkflowService: FormWorkflowService;
  timelineService: TimelineService;
  reportingAgentSocket: IReportingAgentSocket;
  reportingAgentService: ReportingAgentService;

  user: UserStore;
  workspace: WorkspaceStore;
  profileSettings: ProfileSettingsStore;
  workspaceSettings: WorkspaceSettingsStore;
  workspaceMembers: WorkspaceMembersStore;
  workspaceRoles: WorkspaceRolesStore;
  workspaceCalendars: WorkspaceCalendarsStore;
  workspaceSkills: WorkspaceSkillsStore;
  workspaceAvailability: WorkspaceAvailabilityStore;
  workspaceTeams: WorkspaceTeamsStore;
  workHours: WorkHoursStore;
  projectTemplates: ProjectTemplatesStore;
  workitemTemplateAttachments: WorkitemTemplateAttachmentsStore;
  workspaceTemplates: WorkspaceTemplatesStore;
  capacityPlanning: CapacityPlanningStore;
  intakeStore: IntakeStore;
  dashboard: DashboardStore;
  report: ReportStore;
  project: ProjectStore;
  projectMembers: ProjectMembersStore;
  requestWorkflow: RequestWorkflowStore;
  formWorkflow: FormWorkflowStore;
  instance: InstanceStore;
  router: RouterStore;
  invitation: InvitationStore;
  issue: IssueStore;
  issueViewPreferences: IssueViewPreferencesStore;
  issueDetail: IssueDetailStore;
  eventDraft: EventDraftStore;
  workItemViewPreferences: WorkItemViewPreferencesStore;
  projectsListViewPreferences: ProjectsListViewPreferencesStore;
  programsListViewPreferences: ProgramsListViewPreferencesStore;
  notification: NotificationStore;
  view: ViewStore;
  commandPalette: CommandPaletteStore;
  dependentShiftPrompt: DependentShiftPromptStore;
  timeline: TimelineStore;
  timelinePreferences: TimelinePreferencesStore;
  featureFlags: FeatureFlagStore;
  workspaceCustomFields: WorkspaceCustomFieldsStore;
  processes: ProcessesStore;
  reportingAgent: ReportingAgentStore;

  constructor() {
    this.authService = new AuthService();
    this.userService = new UserService();
    this.workspaceService = new WorkspaceService();
    this.projectService = new ProjectService();
    this.projectCustomFieldsService = new ProjectCustomFieldsService();
    this.workItemCustomFieldsService = new WorkItemCustomFieldsService();
    this.projectMemberService = new ProjectMemberService();
    this.workItemStateService = new WorkItemStateService();
    this.projectStateService = new ProjectStateService();
    this.requestStatusService = new RequestStatusService();
    this.requestReasonService = new RequestReasonService();
    this.favoriteService = new FavoriteService();
    this.myWorkService = new MyWorkService();
    this.instanceService = new InstanceService();
    this.invitationService = new InvitationService();
    this.workItemService = new WorkItemService();
    this.issueService = this.workItemService;
    this.issueArchiveService = new IssueArchiveService();
    this.issueCommentService = new IssueCommentService();
    this.mentionableUsersService = new MentionableUsersService();
    this.linkPreviewService = new LinkPreviewService();
    this.supportAccessService = new SupportAccessService();
    this.issueAttachmentService = new IssueAttachmentService();
    this.issueMetadataService = new IssueMetadataService();
    this.eventService = new EventService();
    this.notificationService = new NotificationService();
    this.viewService = new ViewService();
    this.workspaceRoleService = new WorkspaceRoleService();
    this.workspaceJobRoleService = new WorkspaceJobRoleService();
    this.workspaceExportService = new WorkspaceExportService();
    this.workspaceWebhookService = new WorkspaceWebhookService();
    this.workspaceAIProviderService = new WorkspaceAIProviderService();
    this.lmsConnectionService = new LmsConnectionService();
    this.integrationCapabilityService = new IntegrationCapabilityService();
    this.msTeamsConnectionService = new MsTeamsConnectionService();
    this.msTeamsLinkService = new MsTeamsLinkService();
    this.lmsLinkService = new LmsLinkService();
    this.lmsRecommendationService = new LmsRecommendationService();
    this.lmsResultsService = new LmsResultsService();
    this.workspaceSsoService = new WorkspaceSsoService();
    this.workspacePasswordPolicyService = new WorkspacePasswordPolicyService();
    this.workspaceSessionPolicyService = new WorkspaceSessionPolicyService();
    this.workspaceTeammatesService = new WorkspaceTeammatesService();
    this.workspaceTeamSetupAreasService = new WorkspaceTeamSetupAreasService();
    this.workspaceCalendarService = new WorkspaceCalendarService();
    this.auditService = new AuditService();
    this.workspaceSkillService = new WorkspaceSkillService();
    this.workspaceAvailabilityService = new WorkspaceAvailabilityService();
    this.workspaceCapacityService = new WorkspaceCapacityService();
    this.timeOffService = new TimeOffService();
    this.workspaceTeamService = new WorkspaceTeamService();
    this.workspaceAssetService = new WorkspaceAssetService();
    this.userAssetService = new UserAssetService();
    this.timezoneService = new TimezoneService();
    this.currencyService = new CurrencyService();
    this.worklogService = new WorklogService();
    this.workHoursService = new WorkHoursService();
    this.projectTemplateService = new ProjectTemplateService();
    this.workspaceTemplateService = new WorkspaceTemplateService();
    this.projectCapacityService = new ProjectCapacityService();
    this.intakeService = new IntakeService();
    this.requestActivityService = new IntakeSubmissionActivityService();
    this.requestCommentService = new IntakeSubmissionCommentService();
    this.slaService = new SlaService();
    this.requestPrioritizationService = new RequestPrioritizationService();
    this.automationSettingsService = new AutomationSettingsService();
    this.dashboardService = new DashboardService();
    this.reportService = new ReportService();
    this.capacityRebalanceService = new CapacityRebalanceService();
    this.projectWorkflowService = new ProjectWorkflowService();
    this.programsService = new ProgramsService();
    this.goalsService = new GoalsService();
    this.workspaceCustomFieldService = new WorkspaceCustomFieldService();
    this.workspaceCategoriesService = new WorkspaceCategoriesService();
    this.processesService = new ProcessesService();
    this.permissionService = new PermissionService();
    this.programActivityService = new ProgramActivityService();
    this.programCommentService = new ProgramCommentService();
    this.projectActivityService = new ProjectActivityService();
    this.projectCommentService = new ProjectCommentService();
    this.projectLinkedSubmissionsService = new ProjectLinkedSubmissionsService();
    this.projectV3Service = new ProjectV3Service();
    this.requestWorkflowService = new RequestWorkflowService();
    this.formWorkflowService = new FormWorkflowService();
    this.timelineService = new TimelineService();
    this.reportingAgentSocket = new ReportingAgentSocket();
    this.reportingAgentService = new ReportingAgentService();

    this.user = new UserStore(this);
    this.workspace = new WorkspaceStore(this);
    this.profileSettings = new ProfileSettingsStore(this);
    this.workspaceSettings = new WorkspaceSettingsStore(this);
    this.workspaceMembers = new WorkspaceMembersStore(this);
    this.workspaceRoles = new WorkspaceRolesStore(this);
    this.workspaceCalendars = new WorkspaceCalendarsStore(this);
    this.workspaceSkills = new WorkspaceSkillsStore();
    this.workspaceAvailability = new WorkspaceAvailabilityStore(this);
    this.workspaceTeams = new WorkspaceTeamsStore(this);
    this.workHours = new WorkHoursStore(this);
    this.projectTemplates = new ProjectTemplatesStore(this);
    this.workitemTemplateAttachments = new WorkitemTemplateAttachmentsStore(this);
    this.workspaceTemplates = new WorkspaceTemplatesStore(this);
    this.capacityPlanning = new CapacityPlanningStore(this);
    this.intakeStore = new IntakeStore(this);
    this.dashboard = new DashboardStore(this);
    this.report = new ReportStore(this);
    this.project = new ProjectStore(this);
    this.projectMembers = new ProjectMembersStore();
    this.requestWorkflow = new RequestWorkflowStore(this);
    this.formWorkflow = new FormWorkflowStore(this);
    this.instance = new InstanceStore(this);
    this.router = new RouterStore();
    this.invitation = new InvitationStore(this);
    this.issue = new IssueStore(this);
    this.issueViewPreferences = new IssueViewPreferencesStore(this);
    this.issueDetail = new IssueDetailStore(this);
    this.eventDraft = new EventDraftStore();
    this.workItemViewPreferences = new WorkItemViewPreferencesStore();
    this.projectsListViewPreferences = new ProjectsListViewPreferencesStore();
    this.programsListViewPreferences = new ProgramsListViewPreferencesStore();
    this.notification = new NotificationStore(this);
    this.view = new ViewStore();
    this.commandPalette = new CommandPaletteStore();
    this.dependentShiftPrompt = new DependentShiftPromptStore();
    this.timeline = new TimelineStore(this);
    this.timelinePreferences = new TimelinePreferencesStore();
    this.featureFlags = new FeatureFlagStore(this);
    this.workspaceCustomFields = new WorkspaceCustomFieldsStore();
    this.processes = new ProcessesStore(this, this.processesService);
    this.reportingAgent = new ReportingAgentStore(this);
    // Cache-invalidation hints share the notification stream (NG-2666). The
    // root store lives as long as the tab, so the listener is never removed.
    listenForCacheHints(this);
  }
}
